import { useState, useRef, useEffect } from 'react';
import {
  Check,
  CheckSquare,
  X,
  Star,
  MapPin,
  Eye,
  EyeOff,
  Clock,
  Search,
  Minus as MinusIcon,
  Plus as PlusIcon,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Folder,
  ChevronsDown,
  ChevronsUp,
} from 'lucide-react';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectDataContext';

const formatDateTime = (val) => {
  if (!val) return '';
  // If it's just a date (YYYY-MM-DD)
  if (typeof val === 'string' && val.length === 10 && val.includes('-')) {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (val === todayStr) return 'Today';
    const date = new Date(val + 'T12:00:00');
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  const date = new Date(val);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (isToday) return timeStr;

  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${timeStr}`;
};

export function SortableTaskItem({
  task,
  hiddenCompletedHeaderIds,
  editingTaskId,
  taskEditRef,
  editTaskText,
  editTaskPriority,
  handleSaveEditTask,
  editTaskDueDate,
  defaultLocationBias,
  handleContextMenu,
  linkify,
  deletingTaskId,
  deleteTask,
  moveToRoot,
  toggleTask,
  toggleTaskStar,
  handleMoveTaskPriority,
  dispatch,
  activeId,
  allUsers,
  participants,
  taskFontSize,
  selectedHeaderId,
  collapsedHeaderIds,
  taskInputRef,
  isTouch,
  isInFolderBubble,
  isFirstInGroup,
  isLastInGroup,
  currentView,
}) {
  const isEditing = editingTaskId === task.id;
  const containerRef = useRef(null);
  const isPersonal = currentView === 'home';
  const isHeader = task.isHeader || (task.text || '').trim().endsWith(':');

  useEffect(() => {
    if (!isEditing) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        handleSaveEditTask(
          task.id,
          editTaskText,
          editTaskDueDate,
          task.isHeader,
          editTaskPriority
        );
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    isEditing,
    task.id,
    editTaskText,
    editTaskDueDate,
    task.isHeader,
    editTaskPriority,
    handleSaveEditTask,
    isPersonal,
  ]);

  const isMobile = window.innerWidth < 1024;
  const showButtons = isMobile || isTouch;

  if (isPersonal && !isHeader && !isEditing) {
    // console.log('Rendering priority buttons for:', task.text, task.priority);
  }

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled: isEditing,
    data: { type: 'task', task },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    touchAction: isDragging ? 'none' : 'pan-y',
  };
  const isSelected = selectedHeaderId === task.id;
  const isCollapsed = collapsedHeaderIds?.[task.id];
  const isCompletedHidden = hiddenCompletedHeaderIds?.[task.id];

  return (
    <div
      ref={setNodeRef}
      style={style}
      key={task.id}
      data-testid={`task-item-${task.id}`}
      data-is-header={isHeader ? 'true' : 'false'}
      onClick={(e) => {
        if (isHeader && !e.target.closest('button')) {
          e.stopPropagation();
          if (task.isPriorityHeader || task.isCompletedHeader) {
            dispatch({ type: 'TOGGLE_COLLAPSE_HEADER', payload: task.id });
          } else {
            dispatch({ type: 'SET_SELECTED_HEADER', payload: task.id });
            if (taskInputRef.current) taskInputRef.current.focus();
          }
        }
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (!isEditing && !task.isPriorityHeader && !task.isCompletedHeader)
          dispatch({ type: 'SET_EDIT_TASK', payload: task });
      }}
      className={`group flex items-center justify-between p-1 px-3 transition-all flex ${isInFolderBubble ? `bg-zinc-50/40 mx-1 border-x border-zinc-100` : 'border-b border-zinc-50 hover:bg-zinc-50/50'} ${isFirstInGroup ? 'mt-4 rounded-t-xl border-t border-zinc-200/60 shadow-sm' : ''} ${isLastInGroup ? 'mb-2 rounded-b-xl border-b border-zinc-200/60 shadow-sm' : isInFolderBubble ? 'border-b border-zinc-100/50' : ''} ${isHeader ? 'cursor-pointer' : ''} ${isSelected ? 'bg-blue-50/60 border-blue-100' : ''}`}
    >
      <div
        className="flex items-center gap-2 flex-1 min-w-0"
        {...(!isEditing ? attributes : {})}
        {...(!isEditing ? listeners : {})}
      >
        {!isHeader && (
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              toggleTask(task.id, task.completed);
            }}
            data-testid={`task-checkbox-${task.id}`}
            className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${task.completed ? 'bg-zinc-800 border-zinc-800 text-white' : 'border-zinc-300 hover:border-zinc-400 bg-white'}`}
          >
            {task.completed && <Check size={10} strokeWidth={4} />}
          </button>
        )}
        {isHeader && (
          <div className="flex items-center">
            {!task.isPriorityHeader && !task.isCompletedHeader && (
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch({ type: 'TOGGLE_HIDE_COMPLETED_HEADER', payload: task.id });
                }}
                className={`p-1 transition-colors rounded-lg ${isCompletedHidden ? 'text-zinc-400 hover:text-zinc-600 bg-zinc-100' : 'text-zinc-300 hover:text-zinc-600'}`}
                title={isCompletedHidden ? 'Show Completed Tasks' : 'Hide Completed Tasks'}
              >
                {isCompletedHidden ? (
                  <EyeOff size={14} strokeWidth={2.5} />
                ) : (
                  <Eye size={14} strokeWidth={2.5} />
                )}
              </button>
            )}
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                dispatch({ type: 'TOGGLE_COLLAPSE_HEADER', payload: task.id });
              }}
              className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors"
            >
              {isCollapsed ? (
                <ChevronRight size={16} strokeWidth={3} />
              ) : (
                <ChevronDown size={16} strokeWidth={3} />
              )}
            </button>
          </div>
        )}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div
              ref={containerRef}
              className="flex flex-col gap-2 p-1"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <textarea
                autoFocus
                onFocus={(e) => {
                  const val = e.target.value;
                  e.target.value = '';
                  e.target.value = val;
                }}
                className="w-full bg-white border border-zinc-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-zinc-500 min-h-[80px] font-medium text-zinc-800"
                value={editTaskText}
                onChange={(e) => dispatch({ type: 'SET_EDIT_TASK_TEXT', payload: e.target.value })}
                onKeyDown={(e) => {
                  // Prevent spacebar from triggering dnd-kit lift action
                  if (e.key === ' ') {
                    e.stopPropagation();
                  }
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSaveEditTask(
                      task.id,
                      editTaskText,
                      editTaskDueDate,
                      task.isHeader,
                      editTaskPriority
                    );
                  } else if (e.key === 'Escape') {
                    dispatch({ type: 'SET_EDIT_TASK', payload: null });
                  }
                }}
              />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex flex-col gap-1 mr-2">
                    <span className="text-[8px] font-black uppercase text-zinc-400">
                      Priority
                    </span>
                    <div className="flex gap-1">
                      {['High', 'Medium', 'Low'].map((p) => (
                        <button
                          key={p}
                          onClick={() => dispatch({ type: 'SET_EDIT_TASK_PRIORITY', payload: p })}
                          className={`px-2 py-0.5 rounded text-[9px] font-black transition-all ${editTaskPriority === p ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200'}`}
                        >
                          {p[0]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[8px] font-black uppercase text-zinc-400">Due Date</span>
                    <input
                      type="date"
                      className="bg-zinc-100 border-none rounded-lg px-2 py-1 text-[10px] font-bold text-zinc-600 outline-none focus:ring-2 focus:ring-zinc-200"
                      value={editTaskDueDate ? editTaskDueDate.split('T')[0] : ''}
                      onChange={(e) =>
                        dispatch({ type: 'SET_EDIT_TASK_DUE_DATE', payload: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-[150px]">
                    {/* Empty spacer or remove entirely if layout allows */}
                  </div>
                </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTask(task.id);
                      }}
                      className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-red-400 hover:text-red-600 mr-auto"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => dispatch({ type: 'SET_EDIT_TASK', payload: null })}
                      className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() =>
                        handleSaveEditTask(
                          task.id,
                          editTaskText,
                          editTaskDueDate,
                          task.isHeader,
                          editTaskPriority
                        )
                      }
                      className="px-4 py-1.5 bg-zinc-900 text-white text-[10px] font-black uppercase tracking-widest rounded-lg shadow-lg hover:bg-black transition-all active:scale-95"
                    >
                      Save
                    </button>
                  </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-start gap-2 w-full min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <div
                      data-testid={`task-text-${task.id}`}
                      onClick={(e) => {
                        if (isHeader) {
                          e.stopPropagation();
                          dispatch({ type: 'SET_SELECTED_HEADER', payload: task.id });
                          if (taskInputRef.current) taskInputRef.current.focus();
                        } else {
                          e.stopPropagation();
                          dispatch({ type: 'SET_EDIT_TASK', payload: task });
                        }
                      }}
                      className={`break-all break-words ${isHeader ? 'font-black text-zinc-900 uppercase tracking-tight cursor-pointer hover:text-blue-600 transition-colors' : task.completed ? 'text-zinc-400 font-light cursor-pointer' : 'text-zinc-800 font-medium cursor-pointer'}`}
                      style={{ fontSize: `${isHeader ? taskFontSize - 1 : taskFontSize}px` }}
                      dangerouslySetInnerHTML={{ __html: linkify(task.text) }}
                    />
                    {task.projectName && !isHeader && (
                      <span className="text-[9px] font-medium text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded ml-1">
                        {task.projectName}
                      </span>
                    )}
                  </div>
                  {!isHeader && (() => {
                    const now = new Date();
                    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                    let displayDueDate = task.dueDate;
                    if (isPersonal) {
                      if (!task.dueDate || task.dueDate.split('T')[0] < todayStr) {
                        displayDueDate = 'Today';
                      }
                    }

                    if (!((!isPersonal && task.isStarred) || task.taskDate || task.dueDate || isPersonal || task.address)) return null;

                    return (
                      <div
                        className="flex items-center gap-1 flex-shrink-0 h-4"
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        {!isPersonal && task.isStarred && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTaskStar(task.id, task.isStarred);
                            }}
                            className="p-0.5 text-yellow-500 hover:text-yellow-600 transition-colors"
                          >
                            <Star size={12} strokeWidth={2.5} className="fill-yellow-500" />
                          </button>
                        )}
                        {!isPersonal && task.taskDate && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch({ type: 'SET_EDIT_TASK', payload: task });
                            }}
                            className="flex items-center gap-1 px-1.5 py-0 bg-blue-100 text-blue-700 text-[8px] rounded border border-blue-200 font-bold hover:bg-blue-200 transition-colors whitespace-nowrap h-3.5"
                          >
                            <span>Task: {formatDateTime(task.taskDate)}</span>
                          </button>
                        )}
                        {(task.dueDate || isPersonal) && displayDueDate !== 'Today' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch({ type: 'SET_EDIT_TASK', payload: task });
                            }}
                            className="flex items-center gap-1 px-1.5 py-0 bg-red-100 text-red-700 text-[8px] rounded border border-red-200 font-bold hover:bg-red-200 transition-colors whitespace-nowrap h-3.5"
                          >
                            <Clock size={10} strokeWidth={2.5} />
                            <span>
                              Due: {formatDateTime(task.dueDate)}
                            </span>
                          </button>
                        )}
                        {(task.venueName || task.address) && (
                          <div className="flex flex-col gap-0 min-w-0">
                            <div className="flex items-center gap-1 px-1.5 py-0 bg-zinc-100 text-zinc-500 text-[8px] rounded border border-zinc-200 font-black whitespace-nowrap h-3.5">
                              <MapPin size={10} strokeWidth={2.5} />
                              <span className="max-w-[150px] truncate">{task.venueName || task.address}</span>
                            </div>
                            {task.venueName && task.address && (
                              <span className="text-[7px] text-zinc-400 font-bold pl-1.5 truncate max-w-[150px]">
                                {task.address}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div
        className={`flex items-center gap-1 transition-opacity ${showButtons ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {!isEditing && !isPersonal && !task.isStarred && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleTaskStar(task.id, task.isStarred);
            }}
            className="text-zinc-300 hover:text-yellow-500 p-1"
          >
            <Star size={14} strokeWidth={2.5} />
          </button>
        )}
        {task.projectName && !isHeader && !isEditing && (
          <div className="flex flex-col -space-y-1.5 opacity-40 hover:opacity-100 transition-opacity">
            {task.priority === 'High' && (
              <>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'Medium')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to Medium"
                >
                  <ChevronDown size={14} strokeWidth={3} />
                </button>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'Low')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to Low"
                >
                  <ChevronsDown size={14} strokeWidth={3} />
                </button>
              </>
            )}
            {task.priority === 'Medium' && (
              <>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'High')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to High"
                >
                  <ChevronUp size={14} strokeWidth={3} />
                </button>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'Low')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to Low"
                >
                  <ChevronDown size={14} strokeWidth={3} />
                </button>
              </>
            )}
            {task.priority === 'Low' && (
              <>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'High')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to High"
                >
                  <ChevronsUp size={14} strokeWidth={3} />
                </button>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'Medium')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to Medium"
                >
                  <ChevronUp size={14} strokeWidth={3} />
                </button>
              </>
            )}
          </div>
        )}
        {!task.projectName && !isHeader && !isEditing && (
          <div className="flex flex-col -space-y-1.5 opacity-40 hover:opacity-100 transition-opacity">
            {task.priority === 'High' && (
              <>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'Medium')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to Medium"
                  data-testid={`move-task-medium-${task.id}`}
                >
                  <ChevronDown size={14} strokeWidth={3} />
                </button>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'Low')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to Low"
                  data-testid={`move-task-low-${task.id}`}
                >
                  <ChevronsDown size={14} strokeWidth={3} />
                </button>
              </>
            )}
            {task.priority === 'Medium' && (
              <>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'High')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to High"
                  data-testid={`move-task-high-${task.id}`}
                >
                  <ChevronUp size={14} strokeWidth={3} />
                </button>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'Low')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to Low"
                  data-testid={`move-task-low-${task.id}`}
                >
                  <ChevronDown size={14} strokeWidth={3} />
                </button>
              </>
            )}
            {task.priority === 'Low' && (
              <>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'High')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to High"
                  data-testid={`move-task-high-${task.id}`}
                >
                  <ChevronsUp size={14} strokeWidth={3} />
                </button>
                <button
                  onClick={() => handleMoveTaskPriority(task.id, 'Medium')}
                  className="text-zinc-400 hover:text-zinc-900"
                  title="Move to Medium"
                  data-testid={`move-task-medium-${task.id}`}
                >
                  <ChevronUp size={14} strokeWidth={3} />
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TasksView({
  path,
  currentView,
  hiddenCompletedHeaderIds,
  activeColorHex,
  dispatch,
  personalTasks,
  tasks,
  allTasks,
  showCompletedTasks,
  editingTaskId,
  taskEditRef,
  editTaskText,
  editTaskPriority,
  handleSaveEditTask,
  editTaskDueDate,
  editTaskVenueName,
  editTaskAddress,
  editTaskAddressLat,
  editTaskAddressLng,
  defaultLocationBias,
  handleContextMenu,
  linkify,
  deletingTaskId,
  deleteTask,
  moveToRoot,
  toggleTask,
  toggleTaskStar,
  handleMoveTaskPriority,
  activeId,
  participants,
  newTask,
  newTaskPriority,
  newTaskDueDate,
  newTaskIsHeader,
  newTaskAssignedTo,
  taskInputRef,
  handleAddTask,
  handleAddPersonalTask,
  isStarredProjectsOpen,
  getRootProject,
  getProjectPath,
  activeChannelId,
  selectedHeaderId,
  taskFontSize,
  collapsedHeaderIds,
  showOnlyStarredTasks,
  activeItem,
  isTouch,
}) {
  const { user, username } = useAuth();
  const { allUsers, channels } = useProject();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showProjectTasks, setShowProjectTasks] = useState(
    () => localStorage.getItem('showProjectTasksInHome') === 'true'
  );
  const [askPriorityForTask, setAskPriorityForTask] = useState(false);

  const handleToggleProjectTasks = () => {
    const newVal = !showProjectTasks;
    setShowProjectTasks(newVal);
    localStorage.setItem('showProjectTasksInHome', newVal.toString());
  };

  useEffect(() => {
    if (selectedHeaderId && taskInputRef?.current) {
      taskInputRef.current.focus();
    }
  }, [selectedHeaderId, taskInputRef]);

  const isPersonal = currentView === 'home';

  const handleFormSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!(newTask || '').trim()) return;
    
    if (!selectedHeaderId || !selectedHeaderId.startsWith('folder-')) {
      setAskPriorityForTask(true);
      return;
    }
    
    if (isPersonal) {
      handleAddPersonalTask(e);
    } else {
      handleAddTask(e);
    }
  };

  // HIERARCHICAL RENDERING LOGIC
  const renderItems = [];
  
  // Collect tasks
  let combinedTasks = [];
  if (isPersonal) {
    combinedTasks = [...(personalTasks || [])];
    if (showProjectTasks) {
      const pTasks = (allTasks || []).filter(t => t.channelId && t.channelId !== activeChannelId);
      // Attach project name to project tasks when in personal view
      const pTasksWithNames = pTasks.map(t => {
        const channel = channels?.find(c => c.id === t.channelId);
        return { ...t, projectName: channel?.name || 'Project' };
      });
      combinedTasks = [...combinedTasks, ...pTasksWithNames];
    }
  } else {
    combinedTasks = [...(tasks || [])];
  }

  // Filter valid tasks
  const validTasks = combinedTasks.filter(t => t && t.id && !t.isHeader && !(t.text || '').trim().endsWith(':'));

  // Group by priority
  ['High', 'Medium', 'Low'].forEach((p) => {
    const priorityTasks = validTasks
      .filter((t) => t.priority === p && !t.completed)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    
    const folderId = `folder-${p}`;
    const isCollapsed = collapsedHeaderIds?.[folderId];

    renderItems.push({
      id: folderId,
      text: `${p} Priority`,
      isHeader: true,
      isPriorityHeader: true,
    });

    if (!isCollapsed) {
      renderItems.push(...priorityTasks.map((t) => ({ ...t, parentId: folderId })));
    }
  });

  // To Organize section (no priority, not completed)
  const toOrganizeTasks = validTasks
    .filter((t) => !t.completed && t.priority !== 'High' && t.priority !== 'Medium' && t.priority !== 'Low')
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  if (toOrganizeTasks.length > 0) {
    const organizeFolderId = 'folder-ToOrganize';
    const isOrganizeCollapsed = collapsedHeaderIds?.[organizeFolderId];

    renderItems.push({
      id: organizeFolderId,
      text: 'To Organize',
      isHeader: true,
      isPriorityHeader: true,
    });

    if (!isOrganizeCollapsed) {
      renderItems.push(...toOrganizeTasks.map((t) => ({ ...t, parentId: organizeFolderId })));
    }
  }

  // Completed section
  if (showCompletedTasks) {
    const completedTasks = validTasks
      .filter((t) => t.completed)
      .sort((a, b) => {
        const timeA = a.completedAt?.toMillis?.() || 0;
        const timeB = b.completedAt?.toMillis?.() || 0;
        return timeB - timeA;
      });

    if (completedTasks.length > 0) {
      const completedFolderId = 'folder-Completed';
      const isCompletedCollapsed = collapsedHeaderIds?.[completedFolderId];

      renderItems.push({
        id: completedFolderId,
        text: 'Completed',
        isHeader: true,
        isCompletedHeader: true,
      });

      if (!isCompletedCollapsed) {
        renderItems.push(...completedTasks.map((t) => ({ ...t, parentId: completedFolderId })));
      }
    }
  }

  const filteredTasks = renderItems.filter((task) => {
    const matchesSearch =
      !searchTerm || (task.text || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStar = !showOnlyStarredTasks || task.isStarred || task.isHeader;

    let isVisible = true;
    if (task.completed && task.parentId && hiddenCompletedHeaderIds?.[task.parentId]) {
      isVisible = false;
    }

    return matchesSearch && matchesStar && isVisible;
  });

  return (
    <div
      className="flex flex-col h-full bg-white min-w-0 border-r border-zinc-200 shadow-sm transition-all duration-500 overflow-hidden"
      style={{ backgroundColor: activeColorHex + '08' }}
    >
      <header
        className="h-10 px-4 border-b flex items-center justify-between flex-shrink-0 bg-white z-10 transition-all duration-500"
        style={{ backgroundColor: activeColorHex + '1A', borderColor: activeColorHex + '33' }}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <CheckSquare
            size={14}
            className="text-zinc-400 flex-shrink-0"
            style={{ color: activeColorHex !== '#ffffff' ? activeColorHex : '' }}
          />
          <h2
            data-testid="tasks-header"
            data-active-channel={activeChannelId}
            className="text-[10px] uppercase tracking-widest font-black text-zinc-800 flex-shrink-0"
          >
            Tasks
          </h2>
          {path && (
            <span className="text-[10px] text-zinc-400 font-normal tracking-normal truncate">
              NYTM{path.map((p) => ` > ${p.name}`).join('')}
            </span>
          )}
          {isPersonal && (
            <button
              onClick={handleToggleProjectTasks}
              className={`text-[10px] font-black tracking-widest ml-2 transition-colors ${showProjectTasks ? 'text-zinc-800' : 'text-zinc-300 hover:text-zinc-500'}`}
            >
              PRJCT
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => dispatch({ type: 'TOGGLE_SHOW_COMPLETED_TASKS' })}
            className={`p-1.5 rounded-lg transition-colors ${showCompletedTasks ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
            title="Toggle Completed Tasks"
          >
            <CheckSquare size={14} />
          </button>
          <button
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            className={`p-1.5 rounded-lg transition-colors ${isSearchOpen ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'}`}
          >
            <Search size={14} />
          </button>
          <div className="w-px h-4 bg-zinc-200 mx-1" />
          <button
            onClick={() =>
              dispatch({ type: 'SET_TASK_FONT_SIZE', payload: Math.max(8, taskFontSize - 1) })
            }
            className="p-1 text-zinc-400 hover:text-zinc-900"
          >
            <MinusIcon size={14} strokeWidth={2.5} />
          </button>
          <span className="text-[10px] text-zinc-500 font-bold w-4 text-center">
            {taskFontSize}
          </span>
          <button
            onClick={() =>
              dispatch({ type: 'SET_TASK_FONT_SIZE', payload: Math.min(32, taskFontSize + 1) })
            }
            className="p-1 text-zinc-400 hover:text-zinc-900"
          >
            <PlusIcon size={14} strokeWidth={2.5} />
          </button>
          <div className="w-px h-4 bg-zinc-200 mx-1" />
          {!isPersonal && (
            <button
              onClick={() => dispatch({ type: 'TOGGLE_SHOW_ONLY_STARRED' })}
              className={`p-1.5 rounded-lg transition-colors ${showOnlyStarredTasks ? 'bg-yellow-50 text-yellow-600' : 'text-zinc-400 hover:text-zinc-600'}`}
              title="Show Starred Only"
            >
              <Star size={14} className={showOnlyStarredTasks ? 'fill-current' : ''} />
            </button>
          )}
        </div>
      </header>

      {isSearchOpen && (
        <div className="px-4 py-2 border-b border-zinc-100 bg-white/50 animate-in slide-in-from-top-1 duration-200">
          <input
            autoFocus
            type="text"
            placeholder="Search tasks..."
            className="w-full bg-white border border-zinc-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-zinc-400 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-0 py-2 custom-scrollbar">
        <SortableContext
          items={(filteredTasks || []).map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {(filteredTasks || []).map((task, i) => {
            const isHeader = task.isHeader || (task.text || '').trim().endsWith(':');
            const nextTask = filteredTasks[i + 1];
            const isInFolderBubble = isHeader || !!task.parentId;
            const isFirstInGroup = isHeader;
            const isLastInGroup =
              isInFolderBubble &&
              (!nextTask ||
                (nextTask.parentId
                  ? nextTask.parentId !== task.parentId && nextTask.parentId !== task.id
                  : true));

            return (
              <SortableTaskItem
                key={task.id}
                task={task}
                isInFolderBubble={isInFolderBubble}
                isFirstInGroup={isFirstInGroup}
                isLastInGroup={isLastInGroup}
                {...{
                  hiddenCompletedHeaderIds,
                  editingTaskId,
                  taskEditRef,
                  editTaskText,
                  editTaskPriority,
                  handleSaveEditTask,
                  editTaskDueDate,
                  editTaskVenueName,
                  editTaskAddress,
                  editTaskAddressLat,
                  editTaskAddressLng,
                  defaultLocationBias,
                  handleContextMenu,
                  linkify,
                  deletingTaskId,
                  deleteTask,
                  moveToRoot,
                  toggleTask,
                  toggleTaskStar,
                  handleMoveTaskPriority,
                  dispatch,
                  activeId,
                  allUsers,
                  participants,
                  taskFontSize,
                  selectedHeaderId,
                  collapsedHeaderIds,
                  taskInputRef,
                  isTouch,
                  currentView,
                }}
              />
            );
          })}
        </SortableContext>
      </div>

      <div className="p-2 border-t border-zinc-100 bg-white">
        <div className="w-full">
          <form
            onSubmit={handleFormSubmit}
            className="relative group"
          >
            <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-xl px-2 py-1 shadow-sm group-focus-within:bg-white group-focus-within:border-zinc-400 group-focus-within:shadow-md transition-all">
              {askPriorityForTask ? (
                <div className="flex items-center gap-2 flex-1 min-w-0 py-1">
                  <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest pl-2">Priority:</span>
                  {['High', 'Medium', 'Low'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        dispatch({ type: 'SET_SELECTED_HEADER', payload: `folder-${p}` });
                        setAskPriorityForTask(false);
                        setTimeout(() => {
                          if (isPersonal) handleAddPersonalTask(new Event('submit'));
                          else handleAddTask(new Event('submit'));
                        }, 50);
                      }}
                      className={`px-3 py-1 rounded text-[10px] font-black uppercase tracking-widest transition-all ${
                        p === 'High' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                        p === 'Medium' ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' :
                        'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setAskPriorityForTask(false)}
                    className="ml-auto p-1 text-zinc-400 hover:text-zinc-600 pr-2"
                  >
                    <X size={14} strokeWidth={3} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {selectedHeaderId && !newTaskIsHeader && (
                      <div className="flex items-center gap-1.5 bg-zinc-200 text-zinc-700 px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider shrink-0 shadow-sm border border-zinc-300 animate-in fade-in slide-in-from-left-2 duration-300">
                        <Folder size={10} strokeWidth={3} />
                        <span className="max-w-[80px] truncate">
                          {selectedHeaderId.includes('High')
                            ? 'High'
                            : selectedHeaderId.includes('Medium')
                              ? 'Medium'
                              : selectedHeaderId.includes('Low')
                                ? 'Low'
                                : (
                                    tasks?.find((t) => t.id === selectedHeaderId) ||
                                    personalTasks?.find((t) => t.id === selectedHeaderId)
                                  )?.text || 'Folder'}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            dispatch({ type: 'SET_SELECTED_HEADER', payload: null });
                          }}
                          className="hover:text-red-600 transition-colors ml-0.5"
                        >
                          <X size={12} strokeWidth={3} />
                        </button>
                      </div>
                    )}

                    <input
                      ref={taskInputRef}
                      data-testid="task-input"
                      type="text"
                      placeholder="Add a task..."
                      className="flex-1 bg-transparent border-none py-2 text-sm outline-none font-medium text-zinc-800 placeholder:text-zinc-400"
                      value={newTask}
                      onChange={(e) => dispatch({ type: 'SET_NEW_TASK', payload: e.target.value })}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="submit"
                      data-testid="add-task-btn"
                      disabled={!(newTask || '').trim()}
                      className="p-1.5 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 hover:shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shrink-0"
                    >
                      <PlusIcon size={22} strokeWidth={2.5} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
